import React, { useState, useEffect } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import Modal, { useModal } from 'modals/dialog'
import { useUsersClientState, useUsers } from 'state/users'
import { useAppSignal } from 'hooks/app-signal'
import { cls } from 'utilities/dom'
import { FlexChildShrink } from 'components/flex'
import { Herows, ModalBody, ContentRow, Status, EmptyContainer } from './s'
import { PickerActions as Actions } from 'modals/s'
import Paragraph from 'components/typography/paragraph'
import Message from 'components/message'
import Filter from './filter'
import People from './people'
import { Plain } from 'components/button'

const PersonPickerContent = ({ namespace, salt, ...props }) => {
    const { formatMessage } = useIntl()
    const { dismiss } = useModal()

    const {
        setQueryOptions,
        filter,
        resetFilter
    } = useUsersClientState(state => ({
        setQueryOptions: state.setQueryOptions,
        filter: state.filter,
        resetFilter: state.resetFilter
    }))

    const {
        items: fetched,
        isFetching,
        isFetched,
        hasNextPage
    } = useUsers()

    const items = props.fixed ?? fetched

    const [picked, setPicked] = useState(null)
    const [resettable, setResettable] = useState(false)
    const [filterResetAt, setFilterResetAt] = useState(Date.now())

    useAppSignal({
        namespace: `${namespace}:advanced-applied`,
        action: advancedApplied => setResettable(advancedApplied)
    })

    const {
        doneAction,
        cancelAction,
        heading = formatMessage({
            id: 'person_action_pick',
            defaultMessage: 'Pick person'
        }),
        caption,
        message,
        heroContent,
        emptyText = formatMessage({
            id: 'empty_filter',
            defaultMessage: '🤷 Sorry, nothing was found with your current filter configuration.'
        })
    } = props

    useEffect(() => {
        !props.fixed && setQueryOptions({ enabled: true })
        return () => setQueryOptions({ enabled: false })
    }, [props.fixed, setQueryOptions])

    useEffect(() => {
        setPicked(props.picked)
        return () => setPicked(null)
    }, [props.picked])

    const pick = person => {
        doneAction({ picked: person }).onClick()
        dismiss()
    }

    const cancel = cancelAction()

    // TODO: Remove this logic when the API supports combining permission
    // filters with other filters supported by UI components
    const filterable = !filter?.permission

    return (
        <>
            <Modal.Header
                heading={heading}
                size="medium"
                caption={caption}>
                <Herows>
                    {!!message && (
                        <FlexChildShrink>
                            <Message
                                {...message}
                                className={cls(['compact', message?.className])} />
                        </FlexChildShrink>
                    )}
                    {!!heroContent && (
                        <FlexChildShrink>
                            {heroContent}
                        </FlexChildShrink>
                    )}
                </Herows>
            </Modal.Header>
            <ModalBody className="fullbleed">
                {(filterable && !props.fixed) && (
                    <Filter
                        namespace={namespace}
                        salt={`${salt}:filter`}
                        key={filterResetAt} />
                )}
                <ContentRow>
                    {(!!items.length && !isFetching) && (
                        <Status className="caption small compact">
                            <FormattedMessage
                                id={(!props.fixed && hasNextPage) ?
                                    'picker_status_scroll_to_load_more' :
                                    'picker_status_all_loaded'
                                }
                                defaultMessage={(!props.fixed && hasNextPage) ?
                                    '{count} loaded – scroll to load more' :
                                    'All {count} are loaded'
                                }
                                values={{ count: items.length }} />
                        </Status>
                    )}
                    {(!items.length && !isFetching && isFetched) && (
                        <EmptyContainer>
                            <Paragraph className="caption small">{emptyText}</Paragraph>
                            {resettable && (
                                <Plain
                                    className="destructive"
                                    onClick={() => {
                                        resetFilter()
                                        setFilterResetAt(Date.now())
                                    }}>
                                    <FormattedMessage
                                        id="action_reset_filters"
                                        defaultMessage="Reset filters" />
                                </Plain>
                            )}
                        </EmptyContainer>
                    )}
                    <People
                        fixed={props.fixed}
                        picked={picked}
                        pick={pick}
                        salt={salt} />
                </ContentRow>
            </ModalBody>
            <Modal.Footer className="fullbleed">
                <Actions className="centered">
                    <Plain
                        className={cancel.effect}
                        onClick={cancel.onClick}>
                        {cancel.label}
                    </Plain>
                </Actions>
            </Modal.Footer>
        </>
    )
}

export default PersonPickerContent